import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import countryDialInfo from '../../country_dial_info.json';
import { paths } from '../../utils/navigation';
import { useOnboardingQuery } from '../../queries/onBoarding';

export type ProfileData = {
  firstName: string;
  lastName: string;
  companyName: string;
  industry: string;
  phoneCountry: string;
  phoneNumber: string;
  role: string;
  area: string;
  useCases: string[];
  personnelCount: string;
  email: string;
};

type OnboardingContextType = {
  currentStep: number;
  totalSteps: number;
  justResumedOnboarding: boolean;
  setJustResumedOnboarding: (justResumed: boolean) => void;
  isInitialMount: React.MutableRefObject<boolean>;
  profileData: ProfileData;
  updateProfileData: (data: Partial<ProfileData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  isStepCompleted: (step: number) => boolean;
  canProceedToStep: (targetStep: number) => boolean;
  navigateWithParams: (path: string, replace?: boolean, paramsToInclude?: object) => void;
  resetProfileData: () => void;
};

const defaultProfileData: ProfileData = {
  firstName: '',
  lastName: '',
  companyName: '',
  industry: '',
  phoneCountry: 'CL',
  phoneNumber: '',
  role: '',
  area: '',
  useCases: [],
  personnelCount: '',
  email: '',
};

// URL and step conversions
const STEP_PARAM_MAP = {
  step1: 1,
  step2: 2,
  step3: 3,
  step4: 4,
  step5: 5
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [profileData, setProfileData] = useState<ProfileData>(defaultProfileData);
  const [justResumedOnboarding, setJustResumedOnboarding] = useState<boolean>(true);
  const totalSteps = 5;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const firstHydrationCompleted = useRef(false);
  const processingNavigation = useRef(false);
  const { data: onboardingData, isLoading: onboardingDataLoading } = useOnboardingQuery();

  // A utility function to navigate with tracking parameters
  const navigateWithParams = useCallback(
    (path: string, replace = false, paramsToInclude: object = {}) => {
      const trackingParams = new URLSearchParams();
      const paramsToPreserve = ['__hstc', '__hssc', '__hsfp', '_gl', 'returnTo'];

      paramsToPreserve.forEach((param) => {
        if (searchParams.has(param)) {
          trackingParams.append(param, searchParams.get(param)!);
        }
      });

      if (paramsToInclude) {
        Object.entries(paramsToInclude).forEach(([key, value]) => {
          if (trackingParams.has(key)) {
            trackingParams.set(key, value);
          } else {
            trackingParams.append(key, value);
          }
        });
      }

      const queryString = trackingParams.toString();
      const destinationPath = queryString ? `${path}?${queryString}` : path;

      navigate(destinationPath, { replace });
    },
    [navigate, searchParams],
  );

  // Hydrate profile data from onboardingData once it is available
  useEffect(() => {
    if (!onboardingData) return;

    // Find country code from phone number prefix
    let phoneCountry = 'CL';
    if (onboardingData.phone_number) {
      const country = countryDialInfo.find((code) => {
        if (!onboardingData.phone_number.startsWith(code.dial_code)) return false;
        const nationalNumber = onboardingData.phone_number.slice(code.dial_code.length);
        return nationalNumber.length >= 6;
      });
      if (country) {
        phoneCountry = country.code;
      }
    }

    setProfileData({
      firstName: onboardingData.first_name || '',
      lastName: onboardingData.last_name || '',
      companyName: onboardingData.company_name || '',
      industry: onboardingData.industry || '',
      phoneCountry,
      phoneNumber: onboardingData.phone_number || '',
      role: onboardingData.role || '',
      area: onboardingData.area || '',
      useCases: onboardingData.use_cases || [],
      personnelCount: onboardingData.potential_users || '',
      email: onboardingData.email || '',
    });

    firstHydrationCompleted.current = true;
  }, [onboardingDataLoading]);

  const resetProfileData = useCallback(() => {
    setProfileData(defaultProfileData);
    setCurrentStep(1);
  }, []);

  // Helper functions for navigation and step management
  const getStepFromParam = (param?: string): number => {
    if (!param) return 0;

    // Check if it's a known step name
    const knownStep = STEP_PARAM_MAP[param as keyof typeof STEP_PARAM_MAP];
    if (knownStep !== undefined) return knownStep;

    // Try to parse as a number
    const numericStep = parseInt(param);
    if (!isNaN(numericStep) && numericStep >= 1 && numericStep <= totalSteps) {
      return numericStep;
    }

    return 0; // Default
  };

  const getParamFromStep = (stepNum: number): string => {
    return `step${stepNum}`;
  };

  const getCurrentUrlStep = (): number => {
    const stepParam = location.pathname.split('/').pop();
    return getStepFromParam(stepParam);
  };

  // Step validation functions
  const isStepCompleted = useCallback(
    (step: number): boolean => {
      // Simple lookup based on step requirements
      const validators = {
        1: () =>
          !!profileData.firstName &&
          !!profileData.lastName &&
          !!profileData.companyName &&
          !!profileData.industry &&
          !!profileData.phoneNumber,
        2: () => !!profileData.role,
        3: () => !!profileData.area,
        4: () => profileData.useCases.length > 0,
        5: () => !!profileData.personnelCount
      };

      const validator = validators[step as keyof typeof validators];
      return validator ? validator() : false;
    },
    [profileData]
  );

  const canProceedToStep = useCallback(
    (targetStep: number): boolean => {
      // Always allow backward navigation
      if (targetStep <= currentStep) return true;

      // For forward navigation, check if previous steps are completed
      for (let i = 0; i < targetStep; i++) {
        if (!isStepCompleted(i)) return false;
      }

      return true;
    },
    [currentStep, isStepCompleted]
  );

  const getHighestCompletedStep = (): number => {
    // Start from the lowest step and work forwards ensuring we check every step
    for (let i = 1; i <= totalSteps; i++) {
      if (!isStepCompleted(i)) return i - 1;
    }
    return 1;
  };

  const getNextAllowedStep = (requestedStep: number): number => {
    // Special cases for early steps
    if (requestedStep <= 1) return 1;

    // Normal case - check if can proceed
    if (canProceedToStep(requestedStep)) return requestedStep;

    // Otherwise, find highest completed + 1
    const highestCompleted = getHighestCompletedStep();
    return Math.max(1, Math.min(highestCompleted + 1, totalSteps));
  };

  const syncUrlToStep = (step: number) => {
    const param = getParamFromStep(step);
    return `/onboarding/${param}`;
  };

  const navigateToStep = (step: number, replace = true) => {
    if (step <= 0) {
      navigateWithParams(paths.auth.signUp, replace);
      return;
    }

    const stepPath = syncUrlToStep(step);
    const params = new URLSearchParams();
    
    const paramsToPreserve = ['__hstc', '__hssc', '__hsfp', '_gl', 'returnTo'];
    paramsToPreserve.forEach((param) => {
      if (searchParams.has(param)) {
        params.append(param, searchParams.get(param)!);
      }
    });
    
    const destinationPath = params.toString() ? `${stepPath}?${params.toString()}` : stepPath;
    
    navigate(destinationPath, { replace });
    setCurrentStep(step);
  };

  // CRUD operations for profile data and step navigation
  const updateProfileData = useCallback((data: Partial<ProfileData>) => {
    setProfileData((prev) => ({ ...prev, ...data }));
  }, []);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setJustResumedOnboarding(false);
      navigateToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setJustResumedOnboarding(false);
      navigateToStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      navigateToStep(step);
    }
  };

  // Main routing and navigation effect
  useEffect(() => {
    if (onboardingDataLoading) return;
    // Wait for profile hydration if we have onboarding data but have not applied it yet
    if (onboardingData && !firstHydrationCompleted.current) return;
    if (profileData.email === '') return;
    // Initialization on mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      let requestedStep = getCurrentUrlStep();
      let modifiedRequestedStep = false;
      if (requestedStep === 0) {
        requestedStep = totalSteps;
        modifiedRequestedStep = true;
      }
      const allowedStep = getNextAllowedStep(requestedStep);

      if (allowedStep < 0) {
        navigate(`${paths.auth.signUp}${location.search}`, { replace: true });
        return;
      }

      if (allowedStep !== requestedStep || modifiedRequestedStep) {
        navigateToStep(allowedStep);
      } else {
        setCurrentStep(allowedStep);
      }
      return;
    }

    // Standard route/step sync
    if (processingNavigation.current) return;

    const urlStep = getCurrentUrlStep();

    // If URL and state are out of sync
    if (urlStep !== currentStep) {
      processingNavigation.current = true;

      // Forward navigation - check if allowed
      if (urlStep > currentStep) {
        const allowedStep = getNextAllowedStep(urlStep);

        if (allowedStep <= 0) {
          navigate(`${paths.auth.signUp}${location.search}`, { replace: true });
          processingNavigation.current = false;
          return;
        }

        if (allowedStep !== urlStep) {
          navigateToStep(allowedStep);
        } else {
          setCurrentStep(urlStep);
        }
      } else {
        // Backward navigation always allowed
        setCurrentStep(urlStep);
      }
      processingNavigation.current = false;
    }
  }, [profileData, onboardingDataLoading, currentStep]);

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        totalSteps,
        justResumedOnboarding,
        setJustResumedOnboarding,
        isInitialMount,
        profileData,
        updateProfileData,
        nextStep,
        prevStep,
        goToStep,
        isStepCompleted,
        canProceedToStep,
        navigateWithParams,
        resetProfileData,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
